import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useSession } from '../auth/useSession'
import { KpiCard } from '../cc/KpiCard'
import { KpiStrip } from '../cc/KpiStrip'
import { EYEBROW, PANEL } from '../cc/tokens'
import { defaultSurfaceFor, fleetPills, pathFor, surfacePills, type FleetId } from '../cc/fleetNav'
import { TopBar } from '../cc/TopBar'
import { GlobalMap, FLEET_LABEL, STATE_LABEL, GLOBAL_STATE_COLOR } from './GlobalMap'
import { tally, useGlobalFleet, type GlobalFleet, type GlobalState } from './useGlobalApi'
import { EIM_STATUS_STYLE } from '../eim/statusStyle'

function sessionDisplayName(session: ReturnType<typeof useSession>): string | undefined {
  return session.status === 'signed-in' ? session.session.name || session.session.email : undefined
}

/** Derived from FLEET_LABEL (GlobalMap.tsx), not a second hardcoded list -- the
 *  adversarial-review finding that motivated this: FLEET_LABEL/GlobalCounts/GlobalFleet
 *  were each widened for TUPL in three different files, and this one (plus the
 *  count-chip strip below) was the one that got missed, silently dropping TUPL from
 *  every "all fleets" view despite its counts being computed correctly all along. */
export const ALL_GLOBAL_FLEETS = Object.keys(FLEET_LABEL) as GlobalFleet[]
const FILTER_OPTIONS: Array<{ id: GlobalFleet | 'all'; label: string }> = [
  { id: 'all', label: 'All fleets' },
  ...ALL_GLOBAL_FLEETS.map((id) => ({ id, label: FLEET_LABEL[id] })),
]

function FleetFilter({ value, onChange }: {
  value: GlobalFleet | 'all'
  onChange: (v: GlobalFleet | 'all') => void
}): JSX.Element {
  return (
    <div className="flex items-center gap-0.5 rounded-full bg-[#EEF2F7] p-0.5">
      {FILTER_OPTIONS.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-current={value === o.id ? 'page' : undefined}
          className={clsx(
            'rounded-full px-3 py-1 text-[11px] font-medium transition-colors',
            value === o.id
              ? 'bg-white text-[#0F172A] shadow-[0_1px_2px_rgba(15,23,42,0.10)]'
              : 'text-[#64748B] hover:text-[#0F172A]',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Every colour and shape this map uses, in one card -- a supervisory overview combining
 *  three fleets that each already have their own dedicated live map is the one place an
 *  operator genuinely can't assume the palette from context, unlike each single-fleet map
 *  where the KPI strip beside it already names every state in play. */
const LEGEND_STATES: GlobalState[] = ['moving', 'idle', 'stale', 'down']

function MapLegend(): JSX.Element {
  return (
    <div className="pointer-events-none absolute bottom-3 left-3 z-10 flex flex-col gap-2 rounded-[12px] bg-white/92 p-3 shadow-[0_10px_30px_-18px_rgba(15,23,42,0.55)] backdrop-blur">
      <div className="flex flex-col gap-1">
        {LEGEND_STATES.map((s) => (
          <div key={s} className="flex items-center gap-1.5 text-[10.5px] font-medium text-[#475569]">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: GLOBAL_STATE_COLOR[s] }} />
            {STATE_LABEL[s]}
          </div>
        ))}
        {/* Charging gets two swatches, not one -- Ultratech and Ports deliberately draw it
         *  in two different colours (see GlobalMap.tsx's EIM_CHARGING_ICON), so one entry
         *  here would misrepresent whichever fleet it didn't match. */}
        <div className="flex items-center gap-1.5 text-[10.5px] font-medium text-[#475569]">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: GLOBAL_STATE_COLOR.charging }} />
          Charging (Ultratech)
        </div>
        <div className="flex items-center gap-1.5 text-[10.5px] font-medium text-[#475569]">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: EIM_STATUS_STYLE.charging.hex }} />
          Charging (Ports)
        </div>
      </div>
      <div className="h-px bg-[rgba(15,23,42,0.08)]" />
      <div className="flex flex-col gap-1 text-[10.5px] font-medium text-[#475569]">
        <span>Truck · Ultratech, Ports, STAR Cement &amp; Wonder Cement</span>
        <span>Bus · MBMT</span>
      </div>
    </div>
  )
}

/**
 * The unified live map: every reporting vehicle across Ultratech, MBMT, EIM, and STAR Cement on one
 * map of India, one status vocabulary (console/src/map/layers/markers.ts's MarkerState),
 * built as its own page rather than folded into MapCanvas (console/src/map/MapCanvas.tsx)
 * -- that engine's vector basemap and camera limits are cut to the Ultratech corridor
 * specifically (TILE_BOUNDS in map/style.ts) and its whole state model runs through
 * useFleet, which has no notion of a bus or a port truck. A raster OSM basemap with no
 * bounds (the same one EimPortsMap.tsx and MbmtLiveMap.tsx already use) is what makes an
 * all-India view possible at all.
 */
export function GlobalShell(): JSX.Element {
  const navigate = useNavigate()
  const session = useSession()
  const userLabel = sessionDisplayName(session)
  const [filter, setFilter] = useState<GlobalFleet | 'all'>('all')
  const [now, setNow] = useState(() => Date.now())
  const { vehicles, counts, loaded, err } = useGlobalFleet()

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const shown = useMemo(
    () => (filter === 'all' ? vehicles : vehicles.filter((v) => v.fleet === filter)),
    [vehicles, filter],
  )
  const shownCounts = useMemo(() => tally(shown), [shown])

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#E9EEF5]">
      <div className="shrink-0 px-3 pt-3">
        <TopBar
          clock={new Date(now).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
          live
          user={userLabel}
          fleets={fleetPills(true)}
          activeFleet="global"
          onFleet={(id) => navigate(pathFor(id as FleetId, defaultSurfaceFor(id as FleetId)))}
          nav={surfacePills('global')}
          onSettings={() => navigate('/settings')}
        />
      </div>

      <div className="shrink-0 flex items-end justify-between gap-3 px-3 pt-3">
        <div>
          <p className={EYEBROW}>All operations · Ultratech, MBMT, Ports, STAR Cement &amp; Wonder Cement</p>
          <h1 className="font-display text-[22px] font-semibold tracking-tight text-[#0F172A]">
            Global fleet map
          </h1>
        </div>
        <FleetFilter value={filter} onChange={setFilter} />
      </div>

      <div className="min-h-0 flex-1 overflow-hidden p-3">
        <div className="flex h-full flex-col gap-3">
          <KpiStrip>
            <KpiCard label="Vehicles reporting" value={loaded ? shownCounts.total : '—'} tone="info" dot />
            <KpiCard label="Moving" value={loaded ? shownCounts.moving : '—'} tone="ok" dot />
            <KpiCard label="Idle" value={loaded ? shownCounts.idle : '—'} tone="warn" dot />
            <KpiCard label="Charging" value={loaded ? shownCounts.charging : '—'} tone="info" dot />
            <KpiCard
              label="Needs attention"
              value={loaded ? shownCounts.attention : '—'}
              tone={shownCounts.attention > 0 ? 'danger' : 'ok'}
              dot
              sub="offline or breakdown"
            />
          </KpiStrip>

          {err ? (
            <div className="rounded-[12px] border border-[rgba(244,63,94,0.26)] bg-[rgba(244,63,94,0.08)] px-3 py-2 text-[12px] text-[#9F1239]">
              {err} -- showing whatever fleets loaded successfully.
            </div>
          ) : null}

          <section className={clsx(PANEL, 'relative min-h-0 flex-1 overflow-hidden')}>
            <GlobalMap vehicles={vehicles} filter={filter} />
            <MapLegend />
            <div className="pointer-events-none absolute right-3 top-3 z-10 flex gap-1.5">
              {ALL_GLOBAL_FLEETS.map((f) => (
                <span
                  key={f}
                  className="rounded-full bg-white/92 px-2.5 py-1 text-[10.5px] font-semibold text-[#0F172A] shadow-[0_1px_2px_rgba(15,23,42,0.1)] backdrop-blur"
                >
                  {FLEET_LABEL[f]} · {counts[f]}
                </span>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
